import './style.scss';
import { DateTime } from 'luxon';
import type { ITrafficSituation } from '../../../../../models/traffic-situation.model';
import type { TransportMode } from '../../../../../models/transport-mode.enum';
import { TransportModeHelper } from '../../../../../helpers/transport-mode.helper';
import checkCircleGreen from '../../../../../../assets/icons/check-circle-filled-green-600.svg';
import infoCircleBlue from '../../../../../../assets/icons/info-circle-filled-blue-800.svg';
import exclamationTriangleOrange from '../../../../../../assets/icons/exclamation-triangle-filled-orange-500.svg';
import exclamationHexagonRed from '../../../../../../assets/icons/exclamation-hexagon-filled-red-600.svg';
import DataUrlHelper from '../../../../../helpers/data-url.helper';
import { Status } from '../../../../../hooks/fetch.hook';
import { Icon } from '../../../../Shared/Icon';
import { useTexts } from '../../../../../hooks/texts.hook';

function TransportModeTrafficInformation({
  transportMode,
  trafficSituations,
  status,
  hideArrowIcon,
}: {
  transportMode: TransportMode;
  trafficSituations: ITrafficSituation[];
  status: Status;
  hideArrowIcon: boolean;
}) {
  // === HOOKS ===
  const { t } = useTexts();

  // === VARIABLES ===
  const newTrafficSituations = trafficSituations.filter((ts) => ts.startTime > DateTime.now().minus({ hours: 24 }));
  const olderTrafficSituations = trafficSituations.filter((ts) => !(ts.startTime > DateTime.now().minus({ hours: 24 })));
  const newCount = newTrafficSituations.length;
  const olderCount = olderTrafficSituations.length;

  // === HELPER FUNCTIONS ===

  function calculateCssClass(): string {
    if (status !== Status.success) return '';
    if (status === Status.success && newTrafficSituations.some((ts) => ts.severity === 'severe')) return 'transport-mode-traffic-information--severe';
    if (status === Status.success && newTrafficSituations.some((ts) => ts.severity === 'normal')) return 'transport-mode-traffic-information--normal';
    if (status === Status.success && newTrafficSituations.some((ts) => ts.severity === 'slight')) return 'transport-mode-traffic-information--slight';
    return 'transport-mode-traffic-information--none';
  }

  function calculateIconUrl(): string {
    if (status === Status.success && newTrafficSituations.some((ts) => ts.severity === 'severe')) return DataUrlHelper.encode(exclamationHexagonRed);
    if (status === Status.success && newTrafficSituations.some((ts) => ts.severity === 'normal')) return DataUrlHelper.encode(exclamationTriangleOrange);
    if (status === Status.success && newTrafficSituations.some((ts) => ts.severity === 'slight')) return DataUrlHelper.encode(infoCircleBlue);
    return DataUrlHelper.encode(checkCircleGreen);
  }

  function calculateIconAltText(): string {
    if (status === Status.success && newTrafficSituations.some((ts) => ts.severity === 'severe')) return t('Stor påverkan', 'Severe impact');
    if (status === Status.success && newTrafficSituations.some((ts) => ts.severity === 'normal')) return t('Medelstor påverkan', 'Moderate impact');
    if (status === Status.success && newTrafficSituations.some((ts) => ts.severity === 'slight')) return t('Liten påverkan', 'Slight impact');
    return '';
  }

  function calculateDescription(type: 'short' | 'long'): string {
    if (status === Status.loading) return t('Laddar data...', 'Loading data...');
    if (status === Status.failed) return t('Kunde inte hämta data.', 'Error getting data.');
    if (status !== Status.success) return '';
    if (newCount === 0) return type === 'short' ? t('0 nya', '0 new') : t('Inga nya störningar', 'No new disruptions');
    if (newCount === 1) return type === 'short' ? t('1 ny', '1 new') : t('1 ny störning', '1 new disruption');
    return type === 'short' ? t(`${newCount} nya`, `${newCount} new`) : t(`${newCount} nya störningar`, `${newCount} new disruptions`);
  }

  return (
    <div className={`transport-mode-traffic-information ${calculateCssClass()} d-flex flex-wrap align-items-center p-2 bg-gray rounded ${hideArrowIcon ? 'arrow-icon-md' : 'arrow-icon'}`}>
      <div className="transport-mode-traffic-information__transport-mode d-flex align-items-center pe-2 ">
        <Icon {...TransportModeHelper.getIcon(transportMode)} className="me-1" altText="" />
        <span className="fw-bold text-decoration-underline">{TransportModeHelper.translate(transportMode)}</span>
      </div>
      <div className="d-flex flex-grow-1 justify-content-between align-items-center">
        {status === Status.success && newCount === 0 && olderCount === 0 ? (
          <div className="d-flex align-items-center me-2">
            <img src={DataUrlHelper.encode(checkCircleGreen)} alt="" className="transport-mode-traffic-information__icon me-1" />
            <div>{t('Inga störningar', 'No disruptions')}</div>
          </div>
        ) : (
          <div className="d-flex align-items-center me-2">
            {status === Status.success && <img src={calculateIconUrl()} alt={calculateIconAltText()} className="transport-mode-traffic-information__icon me-1" />}
            <span className="visually-hidden">{calculateDescription('long')}</span>
            <span className="d-sm-none" aria-hidden>
              {calculateDescription('short')}
            </span>
            <span className="d-none d-sm-block" aria-hidden>
              {calculateDescription('long')}
            </span>
          </div>
        )}
        {status === Status.success && olderCount > 0 && (
          <div>
            <span className="visually-hidden">{t(`${olderCount} övriga störningar`, `${olderCount} other disruptions`)}</span>
            <span aria-hidden>{t(`${olderCount} övriga`, `${olderCount} other`)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default TransportModeTrafficInformation;
